import type { WixComponentManifest } from '@wix/component-protocol/schema';
import { ManifestStore } from './manifestStore';

import { ManifestDefinitions, EditorPointer } from './types';
import { getElementManifestByInnerElementPath } from './utils/getElementManifestUtils';
import { ManifestUrlResolver } from './ManifestUrlResolver';
import { getManifestValueHandlers } from './utils/getManifestValueHandlers';

export type GetManifestValue = {
  property?: string;
  manifest: WixComponentManifest | null;
};

export class ManifestServices {
  private _manifestStore: ManifestStore;

  constructor(_ManifestUrlResolver: ManifestUrlResolver) {
    this._manifestStore = new ManifestStore(_ManifestUrlResolver);

    this.setManifest = this.setManifest.bind(this);
    this.getManifestsByCompTypes = this.getManifestsByCompTypes.bind(this);
  }

  public getValueFromManifest(
    {
      compType,
      innerElementPath,
    }: {
      compType: string;
      innerElementPath: EditorPointer['innerElementPath'];
    },
    definition: ManifestDefinitions,
    property?: string,
  ) {
    const manifest = getElementManifestByInnerElementPath(
      this._manifestStore.getManifest(compType),
      innerElementPath,
    );

    return getManifestValueHandlers[definition]({ property, manifest });
  }

  public get manifestStore() {
    return this._manifestStore;
  }

  public async loadManifestsByCompTypes(
    compTypes: string[],
    hardUpdate = false,
  ): Promise<string[]> {
    return this._manifestStore.loadManifestsByCompTypes(compTypes, hardUpdate);
  }

  public async loadAndResolveManifestsByCompTypes(compTypes: string[]) {
    const compTypesWithLoadedManifests = await this.loadManifestsByCompTypes(
      compTypes,
    );

    return this.resolveManifestsByCompTypes(compTypesWithLoadedManifests);
  }

  public async forceLoadAndResolveManifestsByCompTypes(compTypes: string[]) {
    const compTypesWithLoadedManifests = await this.loadManifestsByCompTypes(
      compTypes,
      true,
    );

    return this.resolveManifestsByCompTypes(compTypesWithLoadedManifests);
  }

  public async resolveManifestsByCompTypes(compTypes: string[]) {
    return this._manifestStore.resolveManifestsByCompTypes(compTypes);
  }

  public getManifestsByCompTypes(compTypes: string[]) {
    return this._manifestStore.getManifestsByCompTypes(compTypes);
  }

  public setManifest(compType: string, manifest: WixComponentManifest) {
    this._manifestStore.setManifest(compType, manifest);
  }

  public setInitialLoad(initialLoad: Promise<void>) {
    this._manifestStore.setInitialLoad(initialLoad);
  }
}
